import React, { useState, useMemo } from "react";
import { PerformanceBadge } from "../PerformanceBadge";
import { getBandForScore } from "../../constants/performance-bands";
import { RatingCard } from "./RatingCard";
import { SupplierDetailEmptyState } from "./SupplierDetailEmptyState";
import type { SupplierRating } from "../../types/supplier-performance.types";

type ViewMode = "all" | "by-project";

interface RatingsListProps {
	canDelete?: boolean;
	ratings: SupplierRating[];
	onDeleteClick?: (rating: SupplierRating) => void;
}

interface ProjectGroup {
	averageScore: number;
	key: string;
	projectName: string;
	ratings: SupplierRating[];
	rfxNames: string[];
}

const getProjectKey = (rating: SupplierRating): string => {
	// Project-level ratings: group by rfq_project
	if (rating.rfq_project?.id) {
		return `project-${rating.rfq_project.id}`;
	}
	// RFx-level ratings: group by the RFx's parent project if available
	if (rating.rfx_project?.id) {
		return `project-${rating.rfx_project.id}`;
	}
	// Fallback: no project association
	return "no-project";
};

const getProjectName = (rating: SupplierRating): string => {
	if (rating.rfq_project?.name) {
		return rating.rfq_project.name;
	}
	if (rating.rfx_project?.name) {
		return rating.rfx_project.name;
	}
	return "Unassigned";
};

export const RatingsList: React.FC<RatingsListProps> = ({
	ratings,
	canDelete = false,
	onDeleteClick,
}) => {
	const [viewMode, setViewMode] = useState<ViewMode>("all");

	const projectGroups = useMemo((): ProjectGroup[] => {
		const groups = new Map<string, ProjectGroup>();

		for (const rating of ratings) {
			const key = getProjectKey(rating);

			if (!groups.has(key)) {
				groups.set(key, {
					key,
					projectName: getProjectName(rating),
					rfxNames: [],
					averageScore: 0,
					ratings: [],
				});
			}

			const group = groups.get(key)!;
			group.ratings.push(rating);

			// Collect unique RFx names within the group
			if (rating.rfx_name && !group.rfxNames.includes(rating.rfx_name)) {
				group.rfxNames.push(rating.rfx_name);
			}
		}

		for (const group of groups.values()) {
			const scores = group.ratings
				.map((r) => r.overall_score)
				.filter((s): s is number => s !== null);
			if (scores.length > 0) {
				group.averageScore =
					scores.reduce((a, b) => a + b, 0) / scores.length;
			}
		}

		return Array.from(groups.values()).sort(
			(a, b) => b.averageScore - a.averageScore
		);
	}, [ratings]);

	if (ratings.length === 0) {
		return <SupplierDetailEmptyState />;
	}

	return (
		<div data-testid="ratings-list">
			<div className="mb-4 flex items-center gap-2">
				<button
					type="button"
					className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
						viewMode === "all"
							? "bg-blue-600 text-white"
							: "bg-gray-100 text-gray-600 hover:bg-gray-200"
					}`}
					onClick={() => setViewMode("all")}
				>
					All Ratings ({ratings.length})
				</button>
				<button
					type="button"
					className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
						viewMode === "by-project"
							? "bg-blue-600 text-white"
							: "bg-gray-100 text-gray-600 hover:bg-gray-200"
					}`}
					onClick={() => setViewMode("by-project")}
				>
					By Project ({projectGroups.length})
				</button>
			</div>

			{viewMode === "all" && (
				<div className="space-y-3">
					{ratings.map((rating) => (
						<RatingCard
							key={rating.id}
							rating={rating}
							canDelete={canDelete}
							onDeleteClick={onDeleteClick}
						/>
					))}
				</div>
			)}

			{viewMode === "by-project" && (
				<div className="space-y-4">
					{projectGroups.map((group) => {
						const band = getBandForScore(group.averageScore);
						return (
							<div
								key={group.key}
								className="rounded-lg border border-gray-200 bg-white"
							>
								<div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
									<div>
										<div className="text-sm font-semibold text-gray-900">
											{group.projectName}
										</div>
										{group.rfxNames.length > 0 && (
											<div className="text-xs text-gray-500">
												{group.rfxNames.length === 1
													? `RFx: ${group.rfxNames[0]}`
													: `RFx: ${group.rfxNames.join(", ")}`}
											</div>
										)}
										<div className="text-xs text-gray-400">
											{group.ratings.length} rating
											{group.ratings.length !== 1 ? "s" : ""}
										</div>
									</div>
									<div className="text-right">
										<div className="mb-1 text-xs text-gray-500">
											Avg. Score
										</div>
										<PerformanceBadge
											score={group.averageScore || null}
											band={band}
											size="sm"
										/>
									</div>
								</div>
								<div className="space-y-2 p-3">
									{group.ratings.map((rating) => (
										<RatingCard
											key={rating.id}
											rating={rating}
											canDelete={canDelete}
											onDeleteClick={onDeleteClick}
										/>
									))}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
